"use client"

import { useState } from "react"
import { Loader2 } from "lucide-react"
import { useAdminPayoutRequestsQuery } from "@/hooks/queries/partners"
import { useProcessPayoutRequestMutation } from "@/hooks/mutations/partners"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { formatMicroUsd } from "@/lib/billing/utils/format"
import { PARTNER_PAYOUT_STATUSES } from "@/lib/partners/constants"
import type { PartnerPayoutStatus } from "@/lib/partners/types"
import type { AdminPayoutRequestView } from "@/server-actions/admin-partners"
import { RejectPayoutDialog } from "@/components/partners/admin/reject-payout-dialog"
import { formatPartnerDate } from "@/components/partners/format-date"
import { formatPercent } from "@/lib/partners/percent"
import { useTranslations } from "next-intl"




export function PayoutQueue() {

  const tUi = useTranslations("Partners.PartnersAdminPayoutQueue")
  const tCommonUi = useTranslations("Common")

  const [status, setStatus] = useState<PartnerPayoutStatus>(PARTNER_PAYOUT_STATUSES.PENDING)
  const [rejectingId, setRejectingId] = useState<string | null>(null)
  const [completing, setCompleting] = useState<AdminPayoutRequestView | null>(null)
  const requests = useAdminPayoutRequestsQuery(status)
  const processMutation = useProcessPayoutRequestMutation()

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        <span key={tUi("theAmountAlreadyLeftThePartnerSBalance")}>{tUi("theAmountAlreadyLeftThePartnerSBalance")}</span></p>

      <Tabs value={status} onValueChange={value => setStatus(value as PartnerPayoutStatus)}>
        <TabsList>
          <TabsTrigger value={PARTNER_PAYOUT_STATUSES.PENDING}><span key={tUi("pending")}>{tUi("pending")}</span></TabsTrigger>
          <TabsTrigger value={PARTNER_PAYOUT_STATUSES.COMPLETED}><span key={tUi("paid")}>{tUi("paid")}</span></TabsTrigger>
          <TabsTrigger value={PARTNER_PAYOUT_STATUSES.REJECTED}><span key={tUi("rejected")}>{tUi("rejected")}</span></TabsTrigger>
        </TabsList>

        <TabsContent value={status} className="mt-4">
          {requests.isError ? (
            <Alert variant="destructive"><AlertDescription><span key={tUi("unableToLoadPayoutRequestsPleaseRefreshAnd")}>{tUi("unableToLoadPayoutRequestsPleaseRefreshAnd")}</span></AlertDescription></Alert>
          ) : requests.isPending ? (
            <div className="flex min-h-32 items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> <span key={tUi("loadingRequests")}>{tUi("loadingRequests")}</span></div>
          ) : requests.data.length ? (
            <div className="space-y-2">
              {requests.data.map(request => (
                <Card key={request.id}>
                  <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
                    <div className="min-w-48 flex-1">
                      <p className="font-medium break-words">{request.partner_tenant_name}</p>
                      <PayoutAmounts request={request} />
                      {request.admin_note && <p className="mt-1 text-sm text-muted-foreground">{request.admin_note}</p>}
                    </div>
                    {status === PARTNER_PAYOUT_STATUSES.PENDING && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={processMutation.isPending}
                          onClick={() => setCompleting(request)}
                        >
                          <span key={tUi("markAsPaid")}>{tUi("markAsPaid")}</span></Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={processMutation.isPending}
                          onClick={() => setRejectingId(request.id)}
                        >
                          <span key={tUi("reject")}>{tUi("reject")}</span></Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle><span key={tUi("nothingHere")}>{tUi("nothingHere")}</span></CardTitle>
                <CardDescription><span key={tUi("noPayoutRequestsWithThisStatus")}>{tUi("noPayoutRequestsWithThisStatus")}</span></CardDescription>
              </CardHeader>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <RejectPayoutDialog
        requestId={rejectingId}
        onOpenChange={open => !open && setRejectingId(null)}
      />

      <AlertDialog open={Boolean(completing)} onOpenChange={open => !open && setCompleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><span key={tUi("markThisPayoutAsPaid")}>{tUi("markThisPayoutAsPaid")}</span></AlertDialogTitle>
            <AlertDialogDescription>
              {completing
                && `${formatMicroUsd(amountToPay(completing))} to ${completing.partner_tenant_name}. Confirm only after the money actually left. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processMutation.isPending}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
            <AlertDialogAction
              disabled={processMutation.isPending}
              onClick={event => {
                // Radix closes on click; keep it open until the mutation settles.
                event.preventDefault()
                if (!completing) return
                processMutation.mutate(
                  { id: completing.id, decision: "complete" },
                  { onSettled: () => setCompleting(null) },
                )
              }}
            >
              {processMutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              <span key={tUi("markAsPaid")}>{tUi("markAsPaid")}</span></AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

/** What the partner is sent: the held amount less the fee fixed on the request. */
function amountToPay(request: AdminPayoutRequestView): number {
  return request.amount_microusd - request.fee_microusd
}

function PayoutAmounts({ request }: { request: AdminPayoutRequestView }) {

  const tUi = useTranslations("Partners.PartnersAdminPayoutQueue")

  const requested = `Requested ${formatMicroUsd(request.amount_microusd)} on ${formatPartnerDate(request.created_at)}`
  // A rejection returned the whole amount, so neither a fee nor a payment applies.
  if (request.status === PARTNER_PAYOUT_STATUSES.REJECTED) {
    return <p className="mt-1 text-sm text-muted-foreground">{requested}</p>
  }
  const paid = request.status === PARTNER_PAYOUT_STATUSES.COMPLETED
  return (
    <p className="mt-1 flex flex-wrap gap-x-1.5 text-sm text-muted-foreground">
      <span>{requested}</span>
      <span aria-hidden="true">·</span>
      <span>{<span key={tUi("feeValue0Value1", { value0: String(formatMicroUsd(request.fee_microusd)), value1: String(formatPercent(request.fee_percent)) })}>{tUi("feeValue0Value1", { value0: String(formatMicroUsd(request.fee_microusd)), value1: String(formatPercent(request.fee_percent)) })}</span>}</span>
      <span aria-hidden="true">·</span>
      <span key={paid ? tUi("paid") : tUi("toPay")}>{paid ? tUi("paid") : tUi("toPay")}</span>
      <span className="font-medium text-foreground">{formatMicroUsd(amountToPay(request))}</span>
    </p>
  )
}
